import { detectDateOrder, displayName, formatDate, LINK_KINDS, LINK_LABEL, formatPhone, linkText, lifespan, type Graph, type Kin, type KinPack, type Person } from '@lineage/core';
import { Portrait } from '../canvas/PersonCard';
import type { RelationType } from './PersonForm';
import s from './ProfilePanel.module.css';
import { formatDay, useLang, useT } from '../../i18n';

export interface ProfileActions {
  canEdit: boolean;
  /** May remove this card or change its family links (owners: any; editors: only cards they added). */
  canModify: boolean;
  canInvite: boolean;
  /** Newest change to this card, for the "Last edited" line. */
  lastEdit?: { by: string | null; at: number };
  /** Offer "This is me" / "See the tree as…" */
  signedIn: boolean;
  onEdit?: () => void;
  onAdd?: (type: RelationType) => void;
  onInvite?: () => void;
  onThisIsMe?: () => void;
  onSeeAs?: () => void;
  onDelete?: () => void;
  onLinkAll?: (childIds: string[], partnerId: string) => void;
}

interface Props {
  person: Person;
  graph: Graph;
  labels: Map<string, string>;
  /** The relationship words in use, and how this person is related to the viewer (for "how you're related"). */
  pack: KinPack;
  kin?: Kin;
  isMe: boolean;
  isLinkedToAnAccount: boolean;
  actions: ProfileActions;
  onSelect: (id: string) => void;
  onClose: () => void;
}

export function ProfilePanel({ person, graph, labels, pack, kin, actions, onSelect, onClose, isMe }: Props) {
  const t = useT();
  const tp = t.profile;
  const lang = useLang();
  const nm = (id: string) => displayName(graph.people.get(id)!);
  const parents = graph.parents.get(person.id) ?? [];
  const partners = graph.partners.get(person.id) ?? [];
  const kids = graph.children.get(person.id) ?? [];
  const sibs = [...new Set(parents.flatMap((p) => graph.children.get(p) ?? []))].filter((c) => c !== person.id);
  const relation = labels.get(person.id);
  const how = kin ? pack.explain(kin) : '';
  // What to call them (PRD 7.2.4): not for yourself, nor for someone with no family link to you.
  const showCall = kin && !isMe && kin.kind !== 'self' && kin.kind !== 'none';
  const address = showCall ? pack.address(kin, person.firstName) : { forms: [], orByName: false };
  const forms = address.forms;
  const order = detectDateOrder([...graph.people.values()].map((p) => p.born));
  const facts: [string, string | null][] = [
    [tp.born, [formatDate(person.born, order, lang), person.birthPlace].filter(Boolean).join(' · ') || null],
    ...(person.living ? [] : ([[tp.died, formatDate(person.died, order, lang)], [tp.restingPlace, person.restingPlace]] as [string, string | null][])),
    [tp.occupation, person.occupation],
    ...(person.living ? ([[tp.livesIn, person.residence]] as [string, string | null][]) : []),
  ];

  // "Is Sonali also Parth's mother?" — a single-parent child whose parent has another partner.
  const linkPrompts = actions.canModify
    ? partners
        .map((q) => ({ q, kids: kids.filter((k) => (graph.parents.get(k) ?? []).length === 1) }))
        .filter((x) => x.kids.length > 0)
    : [];

  /** A relative's chip: their label in the relationship words (tagged with its language), else a plain role. */
  const chip = (id: string, fallback: string) => {
    const label = labels.get(id);
    return (
      <button key={id + fallback} className={s.chip} type="button" onClick={() => onSelect(id)}>
        <small lang={label ? pack.lang : undefined} dir={label ? pack.dir : undefined}>{label || fallback}</small>
        {nm(id)}
      </button>
    );
  };

  return (
    <aside className={s.panel} aria-label={tp.label(displayName(person))}>
      <div className={s.grab} aria-hidden="true" />
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <button className="btn btn-ghost" type="button" onClick={onClose} aria-label={tp.closeProfile} data-close-profile>✕</button>
      </div>
      <div className={s.hero}>
        <Portrait person={person} size="full" />
        <div className={s.big}>{displayName(person)}</div>
        <div className="muted">{lifespan(person, lang)}</div>
        {relation && <span className="pill" lang={pack.lang} dir={pack.dir}>{relation}</span>}
        {how && <div className={`muted ${s.how}`} lang={pack.lang} dir={pack.dir}>{how}</div>}
      </div>

      <div className="row" style={{ marginTop: 16, justifyContent: 'center' }}>
        {actions.canEdit && <button className="btn btn-sm" type="button" onClick={actions.onEdit}>{tp.edit}</button>}
        {actions.canInvite && <button className="btn btn-sm" type="button" onClick={actions.onInvite}>{tp.invite}</button>}
        {actions.signedIn && !isMe && <button className="btn btn-sm" type="button" onClick={actions.onThisIsMe}>{tp.thisIsMe}</button>}
        {actions.signedIn && !isMe && <button className="btn btn-sm" type="button" onClick={actions.onSeeAs}>{tp.seeAs(person.firstName)}</button>}
      </div>

      {actions.canEdit && (
        <div className={s.section}>
          <h3>{tp.addRelative}</h3>
          <div className="row">
            {(['parent', 'partner', 'child', 'sibling'] as const).map((r) => (
              <button key={r} className="btn btn-sm" type="button" onClick={() => actions.onAdd?.(r)}>+ {t.common.relation[r]}</button>
            ))}
          </div>
        </div>
      )}

      {linkPrompts.map(({ q, kids: ks }) => (
        <div key={q} className={`${s.section} banner`}>
          {tp.alsoParent(nm(q), ks.map(nm).join(tp.and), ((g) => (g === 'female' || g === 'male' ? g : null))(graph.people.get(q)?.gender))}
          <div style={{ marginTop: 8 }}>
            <button className="btn btn-sm" type="button" onClick={() => actions.onLinkAll?.(ks, q)}>{tp.linkAll(person.firstName)}</button>
          </div>
        </div>
      ))}

      {showCall && (
        <section className={s.section} aria-labelledby="what-to-call">
          <h3 id="what-to-call">{tp.whatToCall(person.gender ?? null)}</h3>
          {forms.length > 0 ? (
            <p className={s.callForms} lang={pack.lang} dir={pack.dir}>{forms.join(' · ')}</p>
          ) : (
            <p>{tp.byName(person.firstName ?? null)}</p>
          )}
          {address.orByName && <p className="muted" style={{ fontSize: 13, margin: '0 0 4px' }}>{tp.ifYounger(person.firstName ?? null)}</p>}
          <p className="muted" style={{ fontSize: 13, margin: 0 }}>{tp.callNote}</p>
        </section>
      )}

      <section className={s.section}>
        <h3>{tp.life}</h3>
        <dl className={s.facts}>
          {facts.filter(([, v]) => v).map(([k, v]) => (
            <div key={k} style={{ display: 'contents' }}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
        {facts.every(([, v]) => !v) && <p className="muted">{tp.noDetails}</p>}
      </section>

      <section className={s.section}>
        <h3>{tp.family}</h3>
        <div className={s.chips}>
          {parents.map((id) => chip(id, t.common.relation.parent))}
          {partners.map((id) => chip(id, t.common.relation.partner))}
          {sibs.map((id) => chip(id, t.common.relation.sibling))}
          {kids.map((id) => chip(id, t.common.relation.child))}
        </div>
        {!parents.length && !partners.length && !sibs.length && !kids.length && <p className="muted">{tp.noRelatives}</p>}
      </section>

      {(person.email || person.phone || LINK_KINDS.some((k) => person[k])) && (
        <section className={s.section}>
          <h3>{tp.online}</h3>
          <dl className={s.facts}>
            {person.email && (
              <div style={{ display: 'contents' }}>
                <dt>{tp.email}</dt>
                <dd><a href={`mailto:${person.email}`} style={{ overflowWrap: 'anywhere' }}>{person.email}</a></dd>
              </div>
            )}
            {person.phone && (
              <div style={{ display: 'contents' }}>
                <dt>{tp.phone}</dt>
                <dd><a href={`tel:${person.phone}`}>{formatPhone(person.phone)}</a></dd>
              </div>
            )}
            {LINK_KINDS.filter((k) => person[k]).map((k) => (
              <div key={k} style={{ display: 'contents' }}>
                <dt>{LINK_LABEL[k]}</dt>
                <dd>
                  <a href={person[k]!} target="_blank" rel="noopener noreferrer nofollow ugc" style={{ overflowWrap: 'anywhere' }}>{linkText(k, person[k]!)}</a>
                </dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {person.story && (
        <section className={s.section}>
          <h3>{tp.story}</h3>
          <p className={s.story}>{person.story}</p>
        </section>
      )}

      {actions.lastEdit && (
        <p className="muted" style={{ fontSize: 13, marginTop: 24 }}>
          {tp.lastEdited(actions.lastEdit.by, formatDay(actions.lastEdit.at, lang, { day: 'numeric', month: 'short', year: 'numeric' }))}
        </p>
      )}

      {actions.canModify && (
        <div className={s.section}>
          <button className="btn btn-sm btn-danger" type="button" onClick={actions.onDelete}>{tp.remove}</button>
        </div>
      )}
    </aside>
  );
}
